import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AgrEnvironment, EnvironmentInjector } from '@next/core-lib/environment';

import { Observable } from 'rxjs';

import {
  CropOption,
  GetBbaCancellationRequest,
  GetBbaCropFieldsResponse,
  PaginatedResult,
  UpdateCropFieldDetailsRequest,
} from '../models/bba-cancellation-service.model';

@Injectable({
  providedIn: 'root',
})
export class BbaCancellationService {
  private readonly env = inject<AgrEnvironment>(EnvironmentInjector);
  protected readonly http = inject(HttpClient);
  protected baseUrl = this.env.APIM_BASE_URL + '/arable-advice-service';

  getBbaCropFields({
    cropYear,
    statusFilter,
    page,
    pageSize,
    searchTerm,
    cropFieldName,
    customerName,
  }: GetBbaCancellationRequest): Observable<PaginatedResult<GetBbaCropFieldsResponse>> {
    const params = this.cleanParams({ statusFilter, page, pageSize, searchTerm, cropFieldName, customerName });

    return this.http.get<PaginatedResult<GetBbaCropFieldsResponse>>(`${this.baseUrl}/crop-fields/year/${cropYear}/bba-participating-crop-fields`, {
      params,
    });
  }

  toggleCancellation(cropYear: number, adviceId: number, customerId: number, isCanceling: boolean): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/advices/${cropYear}/fertilization-balance-settings/${adviceId}/set-cancellation-force`, {
      customerId,
      isCanceling,
    });
  }

  toggleIsFertilabInformedOfCancellation(cropYear: number, adviceId: number, customerId: number, isInformed: boolean): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/advices/${cropYear}/fertilization-balance-settings/${adviceId}/set-fertilab-informed-of-cancellations`, {
      customerId,
      isInformed,
    });
  }

  toggleIsAdministrationInformedOfCancellation(cropYear: number, adviceId: number, customerId: number, isInformed: boolean): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/advices/${cropYear}/fertilization-balance-settings/${adviceId}/set-administration-informed-of-cancellations`,
      {
        customerId,
        isInformed,
      },
    );
  }

  sendCancellationMail(cropYear: number, adviceIdsByCustomerIds: Record<number, number>): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/advices/${cropYear}/fertilization-balance-settings/send-cancellation-force`, {
      adviceIdsByCustomerIds,
    });
  }

  getAvailableCrops(): Observable<CropOption[]> {
    return this.http.get<CropOption[]>(`${this.baseUrl}/crop-fields/available-crops`);
  }

  updateCropFieldDetails(request: UpdateCropFieldDetailsRequest): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/crop-fields/correct-crop-field-values`, request);
  }

  private cleanParams(obj: Record<string, string | number>) {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== '' && v !== undefined && v !== null));
  }
}
